import {
  ActivityKindEnum,
  AdFieldBathsEnum,
  AdFieldBedsEnum,
  AdFieldFloorEnum,
  AdFieldFurnitureEnum,
  AdFieldOccupancyEnum,
  AdFieldRenovationEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
  UserKindEnum,
} from '@/types';
import { validateClientRequestTitle } from '@/schemas/clientRequestTitleSchema';
import type {
  ClientRequestAmbiguousField,
  ClientRequestPrefill,
  ClientRequestUnmappedFilter,
  ClientRequestUnmappedFilterCode,
} from '../Ads/utils/clientRequestPrefill';

type UnknownRecord = Record<string, unknown>;

interface OptionalValue<T> {
  ok: boolean;
  value?: T;
}

export interface NormalizedStorefrontClientRequestRouteState {
  source: 'storefront';
  returnTo: string;
  title: string;
  prefill: Partial<ClientRequestPrefill>;
  ambiguousFields: ClientRequestAmbiguousField[];
  unmappedFilters: ClientRequestUnmappedFilter[];
}

const INVALID_VALUE = Symbol('invalid-value');

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isEnumValue = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && values.some((item) => item === value);

const getOptionalValue = <T>(
  source: UnknownRecord,
  key: string,
  isValid: (value: unknown) => value is T
): OptionalValue<T> => {
  const value = source[key];

  if (value === undefined || value === null) return { ok: true };
  if (!isValid(value)) return { ok: false };

  return { ok: true, value };
};

const getOptionalString = (source: UnknownRecord, key: string): OptionalValue<string> => {
  const result = getOptionalValue(source, key, (value): value is string => {
    return typeof value === 'string' && value.trim().length > 0;
  });

  return result.value ? { ok: true, value: result.value.trim() } : result;
};

const getOptionalNumber = (source: UnknownRecord, key: string): OptionalValue<number> =>
  getOptionalValue(
    source,
    key,
    (value): value is number => typeof value === 'number' && Number.isFinite(value)
  );

const getOptionalCoordinate = (source: UnknownRecord, key: string): OptionalValue<string> => {
  const result = getOptionalValue(source, key, (value): value is string => {
    if (typeof value !== 'string' || !value.trim()) return false;

    return Number.isFinite(Number(value.replace(',', '.')));
  });

  return result.value ? { ok: true, value: result.value.trim() } : result;
};

const getOptionalEnum = <T extends string>(
  source: UnknownRecord,
  key: string,
  values: readonly T[]
): OptionalValue<T> =>
  getOptionalValue(source, key, (value): value is T => isEnumValue(values, value));

const getOptionalEnumArray = <T extends string>(
  source: UnknownRecord,
  key: string,
  values: readonly T[]
): OptionalValue<T[]> => {
  const result = getOptionalValue(source, key, (value): value is T[] => {
    return Array.isArray(value) && value.every((item) => isEnumValue(values, item));
  });

  return result.value ? { ok: true, value: [...result.value] } : result;
};

const normalizeLocation = (value: unknown) => {
  if (!isRecord(value)) return null;

  const addressEn = getOptionalString(value, 'addressEn');
  const coordinatesLat = getOptionalCoordinate(value, 'coordinatesLat');
  const coordinatesLong = getOptionalCoordinate(value, 'coordinatesLong');
  const nameEn = getOptionalString(value, 'nameEn');
  const placeId = getOptionalString(value, 'placeId');

  const normalizedLocation = {
    ...(addressEn.ok && addressEn.value ? { addressEn: addressEn.value } : {}),
    ...(coordinatesLat.ok && coordinatesLat.value ? { coordinatesLat: coordinatesLat.value } : {}),
    ...(coordinatesLong.ok && coordinatesLong.value
      ? { coordinatesLong: coordinatesLong.value }
      : {}),
    ...(nameEn.ok && nameEn.value ? { nameEn: nameEn.value } : {}),
    ...(placeId.ok && placeId.value ? { placeId: placeId.value } : {}),
  };

  return Object.keys(normalizedLocation).length ? normalizedLocation : null;
};

const normalizePrefill = (value: unknown): Partial<ClientRequestPrefill> | null => {
  if (value === undefined) return {};
  if (!isRecord(value)) return null;

  const typeOfActivity = getOptionalEnum(value, 'typeOfActivity', Object.values(ActivityKindEnum));
  const areaFrom = getOptionalNumber(value, 'areaFrom');
  const areaTo = getOptionalNumber(value, 'areaTo');
  const baths = getOptionalEnum(value, 'baths', Object.values(AdFieldBathsEnum));
  const bedsArr = getOptionalEnumArray(value, 'bedsArr', Object.values(AdFieldBedsEnum));
  const completionStatus = getOptionalEnum(
    value,
    'completionStatus',
    Object.values(CompletionStatusEnum)
  );
  const coordinatesLat = getOptionalCoordinate(value, 'coordinatesLat');
  const coordinatesLong = getOptionalCoordinate(value, 'coordinatesLong');
  const locationId = getOptionalString(value, 'locationId');
  const nameEn = getOptionalString(value, 'nameEn');
  const period = getOptionalEnum(value, 'period', Object.values(RentPeriodEnum));
  const placeId = getOptionalString(value, 'placeId');
  const priceFrom = getOptionalNumber(value, 'priceFrom');
  const priceTo = getOptionalNumber(value, 'priceTo');
  const propertyKind = getOptionalEnum(value, 'propertyKind', Object.values(PropertyKindEnum));
  const addressEn = getOptionalString(value, 'addressEn');

  return {
    ...(typeOfActivity.ok && typeOfActivity.value !== undefined
      ? { typeOfActivity: typeOfActivity.value }
      : {}),
    ...(areaFrom.ok && areaFrom.value !== undefined ? { areaFrom: areaFrom.value } : {}),
    ...(areaTo.ok && areaTo.value !== undefined ? { areaTo: areaTo.value } : {}),
    ...(baths.ok && baths.value !== undefined ? { baths: baths.value } : {}),
    ...(bedsArr.ok && bedsArr.value !== undefined ? { bedsArr: bedsArr.value } : {}),
    ...(completionStatus.ok && completionStatus.value !== undefined
      ? { completionStatus: completionStatus.value }
      : {}),
    ...(coordinatesLat.ok && coordinatesLat.value ? { coordinatesLat: coordinatesLat.value } : {}),
    ...(coordinatesLong.ok && coordinatesLong.value
      ? { coordinatesLong: coordinatesLong.value }
      : {}),
    ...(locationId.ok && locationId.value ? { locationId: locationId.value } : {}),
    ...(nameEn.ok && nameEn.value ? { nameEn: nameEn.value } : {}),
    ...(period.ok && period.value !== undefined ? { period: period.value } : {}),
    ...(placeId.ok && placeId.value ? { placeId: placeId.value } : {}),
    ...(priceFrom.ok && priceFrom.value !== undefined ? { priceFrom: priceFrom.value } : {}),
    ...(priceTo.ok && priceTo.value !== undefined ? { priceTo: priceTo.value } : {}),
    ...(propertyKind.ok && propertyKind.value !== undefined
      ? { propertyKind: propertyKind.value }
      : {}),
    ...(addressEn.ok && addressEn.value ? { addressEn: addressEn.value } : {}),
  };
};

const normalizeLocationSource = (
  value: unknown
): Extract<ClientRequestAmbiguousField, { field: 'location' }>['sourceValue'] | null => {
  if (!isRecord(value)) return null;

  const location = value.location === undefined ? undefined : normalizeLocation(value.location);
  const locationId = Array.isArray(value.locationId)
    ? value.locationId
        .filter((item): item is string => typeof item === 'string' && !!item.trim())
        .map((item) => item.trim())
    : undefined;

  if (!location && !locationId?.length) return null;

  return {
    ...(location ? { location } : {}),
    ...(locationId?.length ? { locationId } : {}),
  };
};

const normalizeAmbiguousField = (value: unknown): ClientRequestAmbiguousField | null => {
  if (!isRecord(value) || typeof value.field !== 'string' || typeof value.reason !== 'string') {
    return null;
  }

  if (
    value.field === 'propertyKind' &&
    value.reason === 'multiple-values-for-scalar' &&
    Array.isArray(value.sourceValue) &&
    value.sourceValue.every((item) => isEnumValue(Object.values(PropertyKindEnum), item))
  ) {
    return {
      field: 'propertyKind',
      reason: 'multiple-values-for-scalar',
      sourceValue: [...value.sourceValue],
    };
  }

  if (
    value.field === 'baths' &&
    value.reason === 'multiple-values-for-scalar' &&
    Array.isArray(value.sourceValue) &&
    value.sourceValue.every((item) => isEnumValue(Object.values(AdFieldBathsEnum), item))
  ) {
    return {
      field: 'baths',
      reason: 'multiple-values-for-scalar',
      sourceValue: [...value.sourceValue],
    };
  }

  if (
    value.field === 'completionStatus' &&
    value.reason === 'unsupported-combination' &&
    Array.isArray(value.sourceValue) &&
    value.sourceValue.every((item) => isEnumValue(Object.values(CompletionStatusEnum), item))
  ) {
    return {
      field: 'completionStatus',
      reason: 'unsupported-combination',
      sourceValue: [...value.sourceValue],
    };
  }

  if (
    value.field === 'location' &&
    (value.reason === 'incomplete-location' || value.reason === 'multiple-values-for-scalar')
  ) {
    const sourceValue = normalizeLocationSource(value.sourceValue);

    return sourceValue
      ? {
          field: 'location',
          reason: value.reason,
          sourceValue,
        }
      : null;
  }

  return null;
};

const unmappedFilterCodes: ClientRequestUnmappedFilterCode[] = [
  'creator',
  'downPayment',
  'floor',
  'floorsNumFrom',
  'floorsNumTo',
  'furniture',
  'handover',
  'isMyAgency',
  'keywords',
  'multiHandover',
  'myFriends',
  'occupancy',
  'postHandover',
  'renovation',
  'transactionType',
];

const normalizeStringArray = (value: unknown) =>
  Array.isArray(value) && value.every((item) => typeof item === 'string' && item.trim())
    ? value.map((item) => String(item).trim())
    : INVALID_VALUE;

const normalizeEnumArray = <T extends string>(value: unknown, values: readonly T[]) =>
  Array.isArray(value) && value.every((item) => isEnumValue(values, item))
    ? [...value]
    : INVALID_VALUE;

const normalizeUnmappedValue = (
  filter: ClientRequestUnmappedFilterCode,
  value: unknown
): unknown | typeof INVALID_VALUE => {
  switch (filter) {
    case 'transactionType':
      return normalizeEnumArray(value, Object.values(TransactionTypeEnum));
    case 'furniture':
      return normalizeEnumArray(value, Object.values(AdFieldFurnitureEnum));
    case 'renovation':
      return normalizeEnumArray(value, Object.values(AdFieldRenovationEnum));
    case 'occupancy':
      return normalizeEnumArray(value, Object.values(AdFieldOccupancyEnum));
    case 'floor':
      return normalizeEnumArray(value, Object.values(AdFieldFloorEnum));
    case 'multiHandover':
      return normalizeStringArray(value);
    case 'floorsNumFrom':
    case 'floorsNumTo':
    case 'downPayment':
      return typeof value === 'number' && Number.isFinite(value) ? value : INVALID_VALUE;
    case 'isMyAgency':
    case 'myFriends':
    case 'postHandover':
      return typeof value === 'boolean' ? value : INVALID_VALUE;
    case 'keywords':
      return typeof value === 'string' && value.trim() ? value.trim() : INVALID_VALUE;
    case 'handover': {
      if (!isRecord(value)) return INVALID_VALUE;
      const quarter = getOptionalString(value, 'quarter');
      const year = getOptionalString(value, 'year');

      if (!quarter.ok || !year.ok || (!quarter.value && !year.value)) return INVALID_VALUE;

      return {
        ...(quarter.value ? { quarter: quarter.value } : {}),
        ...(year.value ? { year: year.value } : {}),
      };
    }
    case 'creator': {
      if (!isRecord(value)) return INVALID_VALUE;
      const id = getOptionalString(value, 'id');
      const kind = getOptionalEnum(value, 'kind', Object.values(UserKindEnum));
      const name = getOptionalString(value, 'name');

      if (!id.ok || !kind.ok || !name.ok || (!id.value && !kind.value && !name.value)) {
        return INVALID_VALUE;
      }

      return {
        ...(id.value ? { id: id.value } : {}),
        ...(kind.value ? { kind: kind.value } : {}),
        ...(name.value ? { name: name.value } : {}),
      };
    }
  }
};

const normalizeUnmappedFilter = (value: unknown): ClientRequestUnmappedFilter | null => {
  if (
    !isRecord(value) ||
    typeof value.filter !== 'string' ||
    !unmappedFilterCodes.includes(value.filter as ClientRequestUnmappedFilterCode)
  ) {
    return null;
  }

  const filter = value.filter as ClientRequestUnmappedFilterCode;
  const normalizedValue = normalizeUnmappedValue(filter, value.value);

  if (normalizedValue === INVALID_VALUE) return null;

  return {
    filter,
    value: normalizedValue as ClientRequestUnmappedFilter['value'],
  };
};

const normalizeArray = <T>(
  value: unknown,
  normalizeItem: (item: unknown) => T | null
): T[] | null => {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return null;

  const normalizedItems: T[] = [];
  for (const item of value) {
    const normalizedItem = normalizeItem(item);
    if (normalizedItem) normalizedItems.push(normalizedItem);
  }

  return normalizedItems;
};

export const isSafeInternalReturnTo = (value: string): boolean =>
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.startsWith('/\\') &&
  !value.includes('\\') &&
  !Array.from(value).some((character) => character.charCodeAt(0) < 32);

export const normalizeStorefrontClientRequestRouteState = (
  value: unknown
): NormalizedStorefrontClientRequestRouteState | null => {
  if (
    !isRecord(value) ||
    value.source !== 'storefront' ||
    typeof value.returnTo !== 'string' ||
    !isSafeInternalReturnTo(value.returnTo) ||
    typeof value.title !== 'string'
  ) {
    return null;
  }

  const titleValidation = validateClientRequestTitle(value.title);
  if (titleValidation.error) return null;

  const prefill = normalizePrefill(value.prefill);
  const ambiguousFields = normalizeArray(value.ambiguousFields, normalizeAmbiguousField);
  const unmappedFilters = normalizeArray(value.unmappedFilters, normalizeUnmappedFilter);

  if (!prefill || !ambiguousFields || !unmappedFilters) return null;

  const safePrefill: Partial<ClientRequestPrefill> = {
    ...prefill,
    ...(prefill.bedsArr ? { bedsArr: [...prefill.bedsArr] } : {}),
  };

  ambiguousFields.forEach((ambiguousField) => {
    const { field } = ambiguousField;

    if (field === 'propertyKind') delete safePrefill.propertyKind;
    if (field === 'baths') delete safePrefill.baths;
    if (field === 'completionStatus') delete safePrefill.completionStatus;
    if (field === 'location') {
      const displayText =
        safePrefill.addressEn ??
        ambiguousField.sourceValue.location?.addressEn ??
        ambiguousField.sourceValue.location?.nameEn;

      delete safePrefill.addressEn;
      delete safePrefill.coordinatesLat;
      delete safePrefill.coordinatesLong;
      delete safePrefill.locationId;
      delete safePrefill.nameEn;
      delete safePrefill.placeId;
      if (displayText) safePrefill.addressEn = displayText;
    }
  });

  return {
    source: 'storefront',
    returnTo: value.returnTo,
    title: titleValidation.title,
    prefill: safePrefill,
    ambiguousFields,
    unmappedFilters,
  };
};
